import {
  DEFAULT_DUNGEON_OPTIONS,
  type Dungeon,
  type DungeonOptions,
  type Grid,
  type Mover,
  type Vec2,
  createRng,
  generateDungeon,
  hashGrid,
  placeStairs,
  vec2,
} from '@d2/core';

import { isoDepth } from './iso';
import { createMover } from './navigation';

/**
 * The cave, as the renderer needs it — grown from the numbers the server sent,
 * and checked against the server's own checksum before a tile is drawn.
 *
 * Generation itself is core's: the client calls the same `generateDungeon` the
 * room called, from the same seed and the same parameters, so the two cannot
 * disagree about the map. `ARCHITECTURE.md`: *"Levels cost ~40 bytes on the
 * wire. The server picks a seed and sends `{seed, depth, biome, params}`; the
 * client regenerates a byte-identical cave. `hashGrid` lets both sides checksum
 * instead of shipping tiles."* This module is the client's half of that
 * sentence, both parts of it.
 *
 * ## The parameters are the server's, not this module's
 *
 * `buildCave` used to call `generateDungeon(createRng(seed))` and let core's
 * defaults decide the rest, which was right while the client picked its own
 * seed from the URL and nothing else existed. It is wrong the moment a room
 * says what its floor was grown under: a server that ever tunes `wallChance`
 * with depth (`TODO.md` #44) would leave every client quietly drawing a
 * different cave from the same seed. `regrowFloor` therefore takes the
 * parameters off the wire and hands them straight back to core — the defaults
 * remain the *server's* fallback, never a second one applied here.
 *
 * ## Who checks the hash, and what happens when it fails
 *
 * The client is the only side that can check, because it is the only side
 * holding two independently grown grids (`TODO.md` #170). A mismatch means this
 * build and the server disagree about where the rock is, and everything after
 * it — every position, every route, every click — is a lie. So the answer is a
 * **returned result, never a rendered guess**: `regrowFloor` hands back either
 * the cave or the two hashes that failed to match, and the caller refuses the
 * room. Drawing anyway is the one answer that is definitely wrong.
 *
 * Note the check can only ever fail for a real reason — an older `@d2/core`, a
 * different platform's floating point, a tampered bundle — which is exactly why
 * it is worth its one FNV sweep at join and nothing per tick.
 *
 * ## The hash is blind to the stairs, so the stairs are checked separately
 *
 * `hashGrid` answers *"is the rock in the same places"* and that is the whole of
 * what it answers. The way down is not a fact about the rock: `placeStairs` is
 * solved **for a body**, and a cell too tight for a mover is never expanded, so
 * the farthest *reachable* cell is a function of the radius. Core states no
 * radius at all — `PLAYER_RADIUS` is `apps/game`'s and `ENTITY_RADIUS` is
 * `./navigation`'s, two numbers in two packages that agree by nothing stronger
 * than both being `0.5` — so a client that solved the exit for any other body
 * would put it on a different cell **of a grid that hashes identically**
 * (`@d2/schema`'s `LevelState.stairs`, `TODO.md` #479).
 *
 * So `regrowFloor` does the same thing with the cell that it does with the
 * hash: place it independently, compare, and hand over nothing at all when the
 * two disagree. The comparison is made with the very `Mover` the client then
 * walks the floor with, which is handed back beside the cave — a second mover
 * built downstream from a different radius would turn this check into a
 * statement about a body nobody is playing.
 */

/** The dungeon a seed names, under the parameters it was grown with. */
export const buildCave = (seed: number, params: DungeonOptions = DEFAULT_DUNGEON_OPTIONS): Dungeon =>
  generateDungeon(createRng(seed), params);

/**
 * The floor a room is on, in the ~40 bytes it crossed the wire as.
 *
 * Declared structurally rather than as `@d2/schema`'s `LevelState`, for the
 * reason `interpolation.ts` declares `PositionSample` itself: a module that
 * took a decorated schema class could not be unit-tested without
 * `@colyseus/schema`, and the mapping from the wire to these four fields is one
 * function in `./wire` that `dungeonParams` already does most of.
 */
export interface LevelDescriptor {
  /** The whole cave in one number. */
  readonly seed: number;
  /** Which floor of the descent, counting from one. */
  readonly depth: number;
  /** The options the server actually grew the floor under — core's own type. */
  readonly params: DungeonOptions;
  /** `hashGrid` of the grid the server is simulating. */
  readonly gridHash: number;
  /**
   * The cell the server put this floor's stairs down on
   * (`@d2/schema`'s `levelStairs`).
   *
   * The one field on this descriptor that is an **output** rather than an input.
   * Everything else is re-run through the generator; this is the answer to a
   * rule the client can run but cannot run *identically* without knowing how
   * wide the server thinks a body is — see the module note.
   */
  readonly stairs: Vec2;
}

/**
 * The floor a client is standing on, as the renderer needs it.
 *
 * Two facts and no more: the cave to lay out, and the one cell that leads off
 * it. It is a **value that is replaced** rather than edited — a descent hands
 * back a different object — because that is what lets a scene notice the ground
 * moved by comparing identities rather than by being told.
 */
export interface DrawnFloor {
  readonly cave: Dungeon;
  /**
   * Where the way down is, or `null` on a floor that has none.
   *
   * In a room it is never `null`: `regrowFloor` refuses a floor whose exit it
   * cannot agree on, so a drawn floor's stairs are a cell both sides placed. The
   * `null` is the **renderer sandbox**, which has no server to agree with and
   * whose cave `placeStairs` can in principle refuse — and a sandbox with no way
   * down is a click that stays a walk rather than a lie about an exit.
   */
  readonly stairs: Vec2 | null;
}

/** Two grids that were supposed to be the same one, and were not. */
export interface GridMismatch {
  /** The hash the server published for the floor it is simulating. */
  readonly expected: number;
  /** The hash of the grid this build grew from the same seed and parameters. */
  readonly actual: number;
  /** The seed both sides claim to have used. */
  readonly seed: number;
}

/**
 * Two exits from one cave that were supposed to be the same cell, and were not.
 *
 * A separate shape from `GridMismatch` rather than a widened one, because the
 * two say genuinely different things to whoever reads the message: a hash
 * mismatch means the two sides grew different maps, and this means they grew the
 * *same* map and disagree about how wide a body is.
 */
export interface StairsMismatch {
  /** The cell the server published — the exit it is actually simulating. */
  readonly expected: Vec2;
  /**
   * The cell this build placed for the body it walks with, or `null` when it
   * found no way down at all.
   *
   * `null` is `placeStairs`' one refusal and it is never about the map: it means
   * this mover cannot stand on the entrance, so there is no floor here for a
   * body this wide.
   */
  readonly actual: Vec2 | null;
  /** The seed both sides claim to have used. */
  readonly seed: number;
}

/**
 * Why a floor was refused.
 *
 * Tagged rather than two return channels, so the one caller that has to *show* a
 * refusal (`game-canvas.tsx`) keeps one branch and one sentence-writer, and so a
 * third kind of disagreement cannot be added without every reader being made to
 * notice it.
 */
export type FloorMismatch =
  | ({ readonly kind: 'grid' } & GridMismatch)
  | ({ readonly kind: 'stairs' } & StairsMismatch);

export type FloorCheck =
  | {
      readonly ok: true;
      readonly cave: Dungeon;
      /** The body the floor was verified for, and the body the client then walks with. */
      readonly mover: Mover;
      /** The way down, as both sides agree it is. */
      readonly stairs: Vec2;
    }
  | { readonly ok: false; readonly mismatch: FloorMismatch };

/**
 * Regrows the floor a room described, and refuses it unless **both** the
 * checksum and the way down agree.
 *
 * A returned result rather than a throw, because "this build disagrees with the
 * server" is an outcome the caller has to *show a player*, not a programmer
 * error (`CLAUDE.md`). Nothing is handed over unless both checks pass, so there
 * is no way to accidentally render an unverified grid or to walk toward an exit
 * the server does not have: the verified floor is the only one that leaves this
 * function.
 *
 * The order is not arbitrary. **The grid is checked first**, because a stairs
 * cell measured on a map this client grew differently is not a disagreement
 * about the stairs at all — reporting it as one would name the symptom and hide
 * the cause.
 */
export const regrowFloor = (level: LevelDescriptor): FloorCheck => {
  const cave = buildCave(level.seed, level.params);
  const actual = hashGrid(cave.grid);

  if (actual !== level.gridHash) {
    return { ok: false, mismatch: { kind: 'grid', expected: level.gridHash, actual, seed: level.seed } };
  }

  // Built here rather than by the caller, and handed back: this is the body the
  // exit is solved for, so it has to be the body the client walks with.
  const mover = createMover(cave.grid);
  const stairs = placeStairs(cave.grid, cave.entrance, mover);

  if (stairs?.x !== level.stairs.x || stairs.y !== level.stairs.y) {
    return { ok: false, mismatch: { kind: 'stairs', expected: level.stairs, actual: stairs, seed: level.seed } };
  }

  return { ok: true, cave, mover, stairs };
};

/** A cell, for a player to read out to whoever asks — or the honest absence of one. */
const describeCell = (cell: Vec2 | null): string => (cell === null ? 'no reachable cell at all' : `${cell.x},${cell.y}`);

/**
 * What a player is told when this build and the server disagree about the floor.
 *
 * Written as a sentence rather than a code, because the person reading it is a
 * player and the only useful actions are "reload" and "tell somebody" — and
 * because the numbers are what the somebody will ask for. The word "different"
 * is the whole message: this is not a network hiccup and retrying the same
 * bundle against the same room will not fix it.
 *
 * The stairs get their own sentence rather than a shared one, because the two
 * failures point an operator at different things: a hash mismatch is an
 * out-of-date `@d2/core`, and a stairs mismatch on a matching hash is the two
 * sides disagreeing about how wide a body is (`ENTITY_RADIUS` against
 * `PLAYER_RADIUS`), which no checksum can see.
 */
export const describeFloorMismatch = (mismatch: FloorMismatch): string =>
  mismatch.kind === 'grid'
    ? `This client and the server disagree about the dungeon. Seed ${mismatch.seed} grew a floor hashing to ${mismatch.actual} here and to ${mismatch.expected} on the server, so the two are not playing the same map. Nothing has been drawn — reload, and if it happens again this client build is out of date.`
    : `This client and the server disagree about the way down. Seed ${mismatch.seed} grew the same floor on both sides, but the stairs are at ${describeCell(mismatch.expected)} on the server and at ${describeCell(mismatch.actual)} here — the exit depends on how wide a body is, which no map checksum can see. Nothing has been drawn — reload, and if it happens again this client build is out of date.`;

/**
 * Whether two descriptors name the **same floor**, field for field.
 *
 * Every field, and not merely the depth. `describeLevel` writes all five
 * together precisely because they describe one fact (`@d2/schema`), so any one
 * of them moving is a floor that has been replaced — and this is what a
 * long-lived client asks of every patch to decide whether the ground under it
 * is still the ground it grew. Comparing only the depth would keep the old cave
 * on screen for any floor change that was not a descent.
 */
export const sameFloor = (a: LevelDescriptor, b: LevelDescriptor): boolean =>
  a.seed === b.seed &&
  a.depth === b.depth &&
  a.gridHash === b.gridHash &&
  a.stairs.x === b.stairs.x &&
  a.stairs.y === b.stairs.y &&
  a.params.width === b.params.width &&
  a.params.height === b.params.height &&
  a.params.wallChance === b.params.wallChance &&
  a.params.smoothingPasses === b.params.smoothingPasses;

export interface CaveTiles {
  /** Every walkable cell, in reading order. Drawn flat, beneath everything. */
  readonly floor: readonly Vec2[];
  /**
   * Every solid cell, grouped by isometric depth band (`x + y`) and indexed by
   * it — so `rockByBand[7]` is the diagonal drawn at `bandDepth(7)`.
   *
   * Grouped rather than listed because a band is the unit the renderer draws
   * rock in: one display object carrying one depth, which entities then sort
   * themselves against individually. The cells of a band cannot overlap each
   * other on screen, and that is what makes the grouping lossless.
   */
  readonly rockByBand: readonly (readonly Vec2[])[];
}

/**
 * Sorts a grid into the two things there are to draw. Every cell lands in
 * exactly one of them, so nothing can be forgotten or drawn twice.
 */
export const splitCaveTiles = (grid: Grid): CaveTiles => {
  const floor: Vec2[] = [];
  const rockByBand: Vec2[][] = Array.from({ length: grid.width + grid.height - 1 }, () => []);

  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      const cell = vec2(x, y);

      if (grid.cells[y * grid.width + x] === true) {
        floor.push(cell);
      } else {
        rockByBand[isoDepth(cell)]?.push(cell);
      }
    }
  }

  return { floor, rockByBand };
};
